import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Pagination,
} from 'react-bootstrap';
import { default as Selector } from 'react-select';
import { toast } from 'react-toastify';
import { FaSearch, FaFileExcel } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles } from '~/Styles/reactSelectStyles';
import { exportarEmExcel } from '~/Utils/Helpers';
import {
  listarGruposEmpresaPosicaoCobranca,
  listarCobradoresPosicaoCobranca,
  consultaImoveisBuscarEmpreendimentos,
  consultarPosicaoCobranca,
} from '~/Services/Service';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

// W-0797 — Imob > Cobrança > Posição Cobrança. Read-only.
// Fiel a ModuloImob/UPosicao.pas (TFposicao) + UPosicao.dfm (captions verbatim).
// RBAC: backend aplica char 86 ('Cobranca Imovel') + deriva sojuris/pezao (125/126/Acesso Juris)
// para escopar a carteira/combo de cobradores. O gate de menu/rota é o char 630 (MigracaoERP —
// portão de migração HML, ortogonal a RBAC; remover na promoção HML→DONE).

// Tipos de financiamento — GroupBox5 (S/B/F). >= 1 obrigatório (RN-03).
const TIPO_FINANCIAMENTO_OPCOES = [
  { valor: 'S', rotulo: 'Sinal' },
  { valor: 'B', rotulo: 'Balão' },
  { valor: 'F', rotulo: 'Financiamento' },
];

// Tipos de título — GroupBox6 (D/1/3). >= 1 obrigatório (RN-04).
const TIPO_TITULO_OPCOES = [
  { valor: 'D', rotulo: 'Boleto' },
  { valor: '1', rotulo: 'Cheque' },
  { valor: '3', rotulo: 'Promissória' },
];

// Sub-tipo de cheque — GroupBox8 (100011/100111/ambos). RN-09.
const SUBTIPO_CHEQUE_OPCOES = [
  { valor: 100011, rotulo: 'Cheque a Receber (100011)' },
  { valor: 100111, rotulo: 'Cheque Devolvido (100111)' },
  { valor: 0, rotulo: 'Ambos' },
];

// Tipos de cobrança — GroupBox4. Mapeamento verbatim UPosicao.pas:516-567:
//   Interna -> [1], Externa -> [2], Tribunal/Notificação -> [3,4], Jurídico -> [5].
// "Sem cobrança" (CheckBox13) é flag separada (sem_cobranca), não um tipo numérico.
const TIPO_COBRANCA_OPCOES = [
  { chave: 'interna', tipos: [1], rotulo: 'Cob. Interna' },
  { chave: 'externa', tipos: [2], rotulo: 'Cob. Externa' },
  { chave: 'tribunal', tipos: [3, 4], rotulo: 'Tribunal de Arbitragem/Notificação' },
  { chave: 'juridico', tipos: [5], rotulo: 'Jurídico' },
];

// Comercialização — GroupBox9 (P/T/A). >= 1 obrigatório (RN-06).
const COMERCIALIZACAO_OPCOES = [
  { valor: 'P', rotulo: 'Próprio' },
  { valor: 'T', rotulo: 'Terceiro' },
  { valor: 'A', rotulo: 'Adm. Carteira' },
];

// Dif. liquidação — GroupBox2 (0/1/2).
const DIFICIL_LIQUIDACAO_OPCOES = [
  { valor: 0, rotulo: 'Considerar todos títulos conforme parâmetros' },
  { valor: 1, rotulo: 'Considera somente os títulos dif. liquidação' },
  { valor: 2, rotulo: 'Não considera os títulos Dif. Liquidação' },
];

// Considerar Títulos — GroupBox7 (Normal/Int.Vendas/Todos).
const PLANO_OPCOES = [
  { valor: 0, rotulo: 'Normal' },
  { valor: 1, rotulo: 'Int.Vendas' },
  { valor: 2, rotulo: 'Todos' },
];

// Valores referente — GroupBox3 (Dívida/Parcela).
const REFERENTE_OPCOES = [
  { valor: true, rotulo: 'Dívida' },
  { valor: false, rotulo: 'Parcela' },
];

// Ordem — GroupBox1 (1..4).
const ORDENACAO_OPCOES = [
  { valor: 1, rotulo: 'Família/Cidade/Endereço' },
  { valor: 2, rotulo: 'Família/Nome Cliente' },
  { valor: 3, rotulo: 'Total decrescente' },
  { valor: 4, rotulo: 'Idade da dívida decrescente' },
];

// Colunas do grid (PROMPT F2) — mapeadas aos aliases reais do backend (snake_case).
const COLUNAS = [
  { campo: 'apelido_conta', titulo: 'Cliente', tipo: 'texto' },
  { campo: 'endereco', titulo: 'Endereço', tipo: 'texto' },
  { campo: 'descricao_cidade', titulo: 'Cidade', tipo: 'texto' },
  { campo: 'contato', titulo: 'Contato', tipo: 'contato' },
  { campo: 'nome', titulo: 'Ativo', tipo: 'texto' },
  { campo: 'codigo_titulo', titulo: 'Título', tipo: 'texto' },
  { campo: 'numero_de_ordem', titulo: 'Parcela', tipo: 'texto' },
  { campo: 'valor', titulo: 'Valor', tipo: 'valor' },
  { campo: 'data_vencimento', titulo: 'Vencimento', tipo: 'data' },
  { campo: 'atraso', titulo: 'Atraso', tipo: 'texto' },
  { campo: 'tarifa', titulo: 'Tarifa', tipo: 'valor' },
  { campo: 'cobrador', titulo: 'Cobrador', tipo: 'texto' },
  { campo: 'total', titulo: 'Total Cliente', tipo: 'valor' },
];

const ITENS_POR_PAGINA = 20;

const formatarData = valor => {
  if (!valor) return '';
  const d = new Date(valor);
  if (isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  return `${dia}/${mes}/${ano}`;
};

// Valor BR no formato #,##0,00 (sem prefixo R$, como no relatório ERP).
const formatarValor = valor => {
  if (valor == null || valor === '') return '';
  const numero = typeof valor === 'string' ? parseFloat(valor) : Number(valor);
  if (isNaN(numero)) return '';
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Número de telefone bruto (NUMERIC no banco) -> string sem casas decimais.
const formatarFone = fone => {
  if (fone == null || fone === '') return '';
  const numero = typeof fone === 'string' ? parseFloat(fone) : Number(fone);
  if (isNaN(numero)) return '';
  return String(Math.trunc(numero));
};

// Contato = DDD + Fone1 / Fone2.
const rotuloContato = item => {
  const ddd = formatarFone(item.ddd);
  const fone1 = formatarFone(item.fone_1);
  const fone2 = formatarFone(item.fone_2);
  const prefixo = ddd ? `(${ddd}) ` : '';
  const fones = [fone1, fone2].filter(f => f !== '').join(' / ');
  return fones ? `${prefixo}${fones}` : '';
};

const valorCelula = (item, coluna) => {
  if (coluna.tipo === 'contato') return rotuloContato(item);
  if (coluna.tipo === 'data') return formatarData(item[coluna.campo]);
  if (coluna.tipo === 'valor') return formatarValor(item[coluna.campo]);
  const v = item[coluna.campo];
  return v == null ? '' : v;
};

const PosicaoCobranca = () => {
  // Combos
  const [gruposOptions, setGruposOptions] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [empreendimentosOptions, setEmpreendimentosOptions] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(
    null
  );
  const [cobradoresOptions, setCobradoresOptions] = useState([]);
  const [cobradorSelecionado, setCobradorSelecionado] = useState(null);
  // Praça = código de cidade (cliente_joia.codigo_cidade). O backend W-0797 não expõe
  // combo de cidades; mantemos campo numérico opcional. [VALIDAR] expor combo de cidades
  // (ex.: endpoint .../pracas) se a UX exigir lista — alinhar com W-0800.
  const [pracaId, setPracaId] = useState('');

  // Tipos / sub-tipos
  const [tiposFinanciamento, setTiposFinanciamento] = useState(['S', 'B', 'F']);
  const [tiposTitulo, setTiposTitulo] = useState(['D', '1', '3']);
  const [subTipoCheque, setSubTipoCheque] = useState(0); // 0 = ambos
  const [cobrancaInterna, setCobrancaInterna] = useState(false);
  const [cobrancaExterna, setCobrancaExterna] = useState(false);
  const [cobrancaTribunal, setCobrancaTribunal] = useState(false);
  const [cobrancaJuridico, setCobrancaJuridico] = useState(false);
  const [semCobranca, setSemCobranca] = useState(false);
  const [tiposComercializacao, setTiposComercializacao] = useState([
    'P',
    'T',
    'A',
  ]);

  // Numéricos
  const [diasAtrasoMin, setDiasAtrasoMin] = useState('30');
  const [diasAtrasoMax, setDiasAtrasoMax] = useState('90');
  const [numParcelas, setNumParcelas] = useState('999');
  const [valorMin, setValorMin] = useState('');
  const [valorMax, setValorMax] = useState('');

  // Opções
  const [referenteDivida, setReferenteDivida] = useState(true);
  const [dificilLiquidacao, setDificilLiquidacao] = useState(0);
  const [plano, setPlano] = useState(0);
  const [incluirSemBoleto, setIncluirSemBoleto] = useState(false);
  const [somenteRefinanciamento, setSomenteRefinanciamento] = useState(false);
  const [ordenacao, setOrdenacao] = useState(1);

  // Resultado
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [jaGerou, setJaGerou] = useState(false);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    carregarGrupos();
    carregarCobradores();
    carregarEmpreendimentos();
  }, []);

  const carregarGrupos = async () => {
    try {
      const res = await listarGruposEmpresaPosicaoCobranca();
      const lista = res ? res : [];
      setGruposOptions(
        lista.map(g => ({
          value: g.codigo_grupo_empresa,
          label: `${g.codigo_grupo_empresa} - ${g.apelido}`,
        }))
      );
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao carregar G.E.');
    }
  };

  const carregarCobradores = async () => {
    try {
      const res = await listarCobradoresPosicaoCobranca();
      const lista = res ? res : [];
      setCobradoresOptions(
        lista.map(c => ({
          value: c.codigo_conta,
          label: `${c.codigo_conta} - ${c.nome_prestador}`,
        }))
      );
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao carregar cobradores.'
      );
    }
  };

  const carregarEmpreendimentos = async () => {
    try {
      const res = await consultaImoveisBuscarEmpreendimentos();
      const lista = res ? res : [];
      setEmpreendimentosOptions(
        lista.map(e => ({
          value: e.codigo_familia,
          label: `${e.codigo_familia} - ${e.nome_da_familia}`,
        }))
      );
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao carregar empreendimentos.'
      );
    }
  };

  // Toggle helper para arrays de tipos (checkbox múltiplo).
  const alternarTipo = (lista, setLista, valor) => {
    if (lista.indexOf(valor) >= 0) {
      setLista(lista.filter(v => v !== valor));
    } else {
      setLista([...lista, valor]);
    }
  };

  // RN-08: cobrador específico ⊻ tipos de cobrança. Com cobrador escolhido, oculta o painel.
  const temCobradorEspecifico = cobradorSelecionado != null;

  // Lista final de tipos de cobrança (1-5) a partir dos checkboxes (mapeamento verbatim).
  const montarTiposCobranca = () => {
    let tipos = [];
    if (cobrancaInterna) tipos = tipos.concat(TIPO_COBRANCA_OPCOES[0].tipos);
    if (cobrancaExterna) tipos = tipos.concat(TIPO_COBRANCA_OPCOES[1].tipos);
    if (cobrancaTribunal) tipos = tipos.concat(TIPO_COBRANCA_OPCOES[2].tipos);
    if (cobrancaJuridico) tipos = tipos.concat(TIPO_COBRANCA_OPCOES[3].tipos);
    return tipos;
  };

  const paraNumeroOuNulo = texto => {
    if (texto == null || texto === '') return null;
    const numero = Number(texto);
    return isNaN(numero) ? null : numero;
  };

  const validar = () => {
    if (tiposFinanciamento.length === 0) {
      toast.warning('Selecione ao menos um tipo de financiamento.');
      return false;
    }
    if (tiposTitulo.length === 0) {
      toast.warning('Selecione ao menos um tipo de título.');
      return false;
    }
    if (diasAtrasoMin === '' || diasAtrasoMax === '') {
      toast.warning('Informe o Nº de Dias de Atraso (de) e (a).');
      return false;
    }
    if (Number(diasAtrasoMax) < Number(diasAtrasoMin)) {
      toast.warning('O Nº de Dias de Atraso (a) deve ser maior ou igual ao (de).');
      return false;
    }
    if (tiposComercializacao.length === 0) {
      toast.warning('Selecione ao menos uma comercialização.');
      return false;
    }
    // RN-05/RN-08: sem cobrador específico, exige >= 1 tipo de cobrança OU "sem cobrança"
    // (UPosicao.pas:603 — raise quando entrou=0 e semcob='').
    if (!temCobradorEspecifico) {
      const algumTipo =
        cobrancaInterna ||
        cobrancaExterna ||
        cobrancaTribunal ||
        cobrancaJuridico;
      if (!algumTipo && !semCobranca) {
        toast.warning('Nenhum tipo de cobrança escolhida!');
        return false;
      }
    }
    return true;
  };

  const gerar = async () => {
    if (!validar()) return;
    try {
      setCarregando(true);
      // Body JSON snake_case (SnakeCaseNamingStrategy global no backend).
      const body = {
        codigo_grupo_empresa: grupoSelecionado ? grupoSelecionado.value : null,
        codigo_familia: empreendimentoSelecionado
          ? empreendimentoSelecionado.value
          : null,
        cobrador_id: cobradorSelecionado ? cobradorSelecionado.value : null,
        praca_id: paraNumeroOuNulo(pracaId),
        tipos_financiamento: tiposFinanciamento,
        tipos_titulo: tiposTitulo,
        // RN-09: 0 = ambos => null (sem filtro). Só envia 100011/100111.
        sub_tipo_cheque: subTipoCheque === 0 ? null : subTipoCheque,
        // RN-08: com cobrador específico não envia tipos de cobrança.
        tipos_cobranca: temCobradorEspecifico ? [] : montarTiposCobranca(),
        tipos_comercializacao: tiposComercializacao,
        dias_atraso_min: diasAtrasoMin === '' ? 30 : Number(diasAtrasoMin),
        dias_atraso_max: diasAtrasoMax === '' ? 0 : Number(diasAtrasoMax),
        num_parcelas: numParcelas === '' ? 999 : Number(numParcelas),
        valor_min: paraNumeroOuNulo(valorMin),
        valor_max: paraNumeroOuNulo(valorMax),
        referente_divida: referenteDivida,
        dificil_liquidacao: dificilLiquidacao,
        plano,
        incluir_sem_boleto: incluirSemBoleto,
        somente_refinanciamento: somenteRefinanciamento,
        sem_cobranca: temCobradorEspecifico ? false : semCobranca,
        ordenacao,
      };
      const res = await consultarPosicaoCobranca(body);
      setRegistros(res ? res : []);
      setJaGerou(true);
      setPagina(1);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao gerar relatório.');
    } finally {
      setCarregando(false);
    }
  };

  const totalRegistros = registros.length;
  const totalPaginas = Math.ceil(totalRegistros / ITENS_POR_PAGINA) || 1;
  const registrosPaginados = registros.slice(
    (pagina - 1) * ITENS_POR_PAGINA,
    pagina * ITENS_POR_PAGINA
  );

  // Total do rodapé = soma da coluna Valor (#,##0,00).
  const totalValor = registros.reduce((acumulado, item) => {
    const numero = Number(item.valor);
    return acumulado + (isNaN(numero) ? 0 : numero);
  }, 0);

  const exportarExcel = () => {
    if (totalRegistros === 0) return;
    const cabecalho = COLUNAS.map(c => c.titulo);
    const linhas = registros.map(item => COLUNAS.map(c => valorCelula(item, c)));
    exportarEmExcel([cabecalho, ...linhas], 'posicao-cobranca.csv');
  };

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const maxBotoes = 5;

    itens.push(
      <Pagination.First
        key="first"
        onClick={() => setPagina(1)}
        disabled={pagina === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPagina(pagina - 1)}
        disabled={pagina === 1}
      />
    );

    let inicioPaginas = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    const fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = inicioPaginas; i <= fimPaginas; i += 1) {
      itens.push(
        <Pagination.Item
          key={i}
          active={i === pagina}
          onClick={() => setPagina(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => setPagina(pagina + 1)}
        disabled={pagina === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => setPagina(totalPaginas)}
        disabled={pagina === totalPaginas}
      />
    );

    return (
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ borderTop: '1px solid #dee2e6' }}
      >
        <small className="text-muted">
          {`Mostrando ${
            totalRegistros > 0 ? (pagina - 1) * ITENS_POR_PAGINA + 1 : 0
          } até ${Math.min(
            pagina * ITENS_POR_PAGINA,
            totalRegistros
          )} de ${totalRegistros} registros`}
        </small>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  return (
    <div>
      <Header title="Posição Cobrança" />
      <Container fluid className="mt-3">
        <Card className="mb-3">
          <Card.Header>
            <strong>Filtros</strong>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-end">
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>G.E.</Form.Label>
                <Selector
                  isClearable
                  styles={customStyles}
                  placeholder="Todos os G.E..."
                  noOptionsMessage={() => 'Nenhum G.E. encontrado'}
                  options={gruposOptions}
                  value={grupoSelecionado}
                  onChange={selecionado =>
                    setGrupoSelecionado(selecionado || null)
                  }
                />
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Empreendimento</Form.Label>
                <Selector
                  isClearable
                  styles={customStyles}
                  placeholder="Todos os empreendimentos..."
                  noOptionsMessage={() => 'Nenhum empreendimento encontrado'}
                  options={empreendimentosOptions}
                  value={empreendimentoSelecionado}
                  onChange={selecionado =>
                    setEmpreendimentoSelecionado(selecionado || null)
                  }
                />
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Cobrador</Form.Label>
                <Selector
                  isClearable
                  styles={customStyles}
                  placeholder="Todos os cobradores..."
                  noOptionsMessage={() => 'Nenhum cobrador encontrado'}
                  options={cobradoresOptions}
                  value={cobradorSelecionado}
                  onChange={selecionado =>
                    setCobradorSelecionado(selecionado || null)
                  }
                />
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Praça de cobrança (cód. cidade)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Todas as praças..."
                  value={pracaId}
                  onChange={e => setPracaId(e.target.value)}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label className="d-block">
                  Tipo de financiamento *
                </Form.Label>
                {TIPO_FINANCIAMENTO_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`tipoFin-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={tiposFinanciamento.indexOf(opcao.valor) >= 0}
                    onChange={() =>
                      alternarTipo(
                        tiposFinanciamento,
                        setTiposFinanciamento,
                        opcao.valor
                      )
                    }
                  />
                ))}
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label className="d-block">Tipo de título *</Form.Label>
                {TIPO_TITULO_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`tipoTit-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={tiposTitulo.indexOf(opcao.valor) >= 0}
                    onChange={() =>
                      alternarTipo(tiposTitulo, setTiposTitulo, opcao.valor)
                    }
                  />
                ))}
              </Col>
              {tiposTitulo.indexOf('1') >= 0 && (
                <Col xs={12} md={4} className="mb-3">
                  <Form.Label className="d-block">Tipo de Cheque</Form.Label>
                  {SUBTIPO_CHEQUE_OPCOES.map(opcao => (
                    <Form.Check
                      inline
                      type="radio"
                      name="subTipoCheque"
                      id={`subCheque-${opcao.valor}`}
                      key={opcao.valor}
                      label={opcao.rotulo}
                      checked={subTipoCheque === opcao.valor}
                      onChange={() => setSubTipoCheque(opcao.valor)}
                    />
                  ))}
                </Col>
              )}
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="d-block">Comercialização *</Form.Label>
                {COMERCIALIZACAO_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`comerc-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={tiposComercializacao.indexOf(opcao.valor) >= 0}
                    onChange={() =>
                      alternarTipo(
                        tiposComercializacao,
                        setTiposComercializacao,
                        opcao.valor
                      )
                    }
                  />
                ))}
              </Col>
              {/* RN-08: Tipo de cobrança visível só quando SEM cobrador específico. */}
              {!temCobradorEspecifico && (
                <Col xs={12} md={6} className="mb-3">
                  <Form.Label className="d-block">Tipo de cobrança</Form.Label>
                  <Form.Check
                    inline
                    type="checkbox"
                    id="cob-interna"
                    label="Cob. Interna"
                    checked={cobrancaInterna}
                    onChange={() => setCobrancaInterna(!cobrancaInterna)}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    id="cob-externa"
                    label="Cob. Externa"
                    checked={cobrancaExterna}
                    onChange={() => setCobrancaExterna(!cobrancaExterna)}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    id="cob-tribunal"
                    label="Tribunal de Arbitragem/Notificação"
                    checked={cobrancaTribunal}
                    onChange={() => setCobrancaTribunal(!cobrancaTribunal)}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    id="cob-juridico"
                    label="Jurídico"
                    checked={cobrancaJuridico}
                    onChange={() => setCobrancaJuridico(!cobrancaJuridico)}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    id="cob-sem"
                    label="Sem cobrança"
                    checked={semCobranca}
                    onChange={() => setSemCobranca(!semCobranca)}
                  />
                </Col>
              )}
            </Row>

            <Row className="align-items-end">
              <Col xs={6} md={3} className="mb-3">
                <Form.Label>Nº Dias Atraso (de) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={diasAtrasoMin}
                  onChange={e => setDiasAtrasoMin(e.target.value)}
                />
              </Col>
              <Col xs={6} md={3} className="mb-3">
                <Form.Label>Nº Dias Atraso (a) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={diasAtrasoMax}
                  onChange={e => setDiasAtrasoMax(e.target.value)}
                />
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Nº parcela(s) em aberto até</Form.Label>
                <Form.Control
                  type="number"
                  value={numParcelas}
                  onChange={e => setNumParcelas(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="align-items-end">
              <Col xs={6} md={3} className="mb-3">
                <Form.Label>Vr. Mínimo</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={valorMin}
                  onChange={e => setValorMin(e.target.value)}
                />
              </Col>
              <Col xs={6} md={3} className="mb-3">
                <Form.Label>Vr. Máximo</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={valorMax}
                  onChange={e => setValorMax(e.target.value)}
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="d-block">Valores referente</Form.Label>
                {REFERENTE_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="radio"
                    name="referente"
                    id={`referente-${String(opcao.valor)}`}
                    key={String(opcao.valor)}
                    label={opcao.rotulo}
                    checked={referenteDivida === opcao.valor}
                    onChange={() => setReferenteDivida(opcao.valor)}
                  />
                ))}
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="d-block">Dif. Liquidação</Form.Label>
                {DIFICIL_LIQUIDACAO_OPCOES.map(opcao => (
                  <Form.Check
                    type="radio"
                    name="dificilLiquidacao"
                    id={`difLiq-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={dificilLiquidacao === opcao.valor}
                    onChange={() => setDificilLiquidacao(opcao.valor)}
                  />
                ))}
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label className="d-block">Considerar Títulos</Form.Label>
                {PLANO_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="radio"
                    name="plano"
                    id={`plano-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={plano === opcao.valor}
                    onChange={() => setPlano(opcao.valor)}
                  />
                ))}
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label className="d-block">Ordem</Form.Label>
                {ORDENACAO_OPCOES.map(opcao => (
                  <Form.Check
                    type="radio"
                    name="ordenacao"
                    id={`ordem-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={ordenacao === opcao.valor}
                    onChange={() => setOrdenacao(opcao.valor)}
                  />
                ))}
              </Col>
            </Row>

            <Row>
              <Col xs={12} className="mb-3">
                <Form.Check
                  inline
                  type="checkbox"
                  id="incluirSemBoleto"
                  label="Incluir títulos sem vínculo com boleto"
                  checked={incluirSemBoleto}
                  onChange={() => setIncluirSemBoleto(!incluirSemBoleto)}
                />
                <Form.Check
                  inline
                  type="checkbox"
                  id="somenteRefinanciamento"
                  label="Lista somente títulos de refinanciamento"
                  checked={somenteRefinanciamento}
                  onChange={() =>
                    setSomenteRefinanciamento(!somenteRefinanciamento)
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} className="d-flex align-items-center flex-wrap">
                <Button
                  variant="primary"
                  className="mr-2"
                  disabled={carregando}
                  onClick={gerar}
                >
                  <FaSearch className="mr-1" />
                  OK
                </Button>
                <Button
                  variant="outline-success"
                  disabled={carregando || totalRegistros === 0}
                  onClick={exportarExcel}
                >
                  <FaFileExcel className="mr-1" />
                  Exportar Excel
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {carregando && <Loading />}

        {!carregando && jaGerou && (
          <Card>
            <Card.Body className="table-responsive">
              {totalRegistros === 0 && (
                <div className="text-center text-muted py-3">
                  Nenhum registro encontrado
                </div>
              )}

              {totalRegistros > 0 && (
                <>
                  <Table className="table table-striped table-bordered table-hover table-sm">
                    <Thead>
                      <Tr className="text-center">
                        {COLUNAS.map(c => (
                          <Th key={c.campo}>{c.titulo}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody className="text-center">
                      {registrosPaginados.map((item, indice) => (
                        <Tr
                          key={`${(pagina - 1) * ITENS_POR_PAGINA +
                            indice}-${item.grupo}-${item.codigo_conta}-${
                            item.codigo_titulo
                          }`}
                        >
                          {COLUNAS.map(c => (
                            <Td key={c.campo}>{valorCelula(item, c)}</Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  <div className="d-flex justify-content-end px-3 py-2">
                    <strong>{`Total: ${formatarValor(totalValor)}`}</strong>
                  </div>
                  {renderizarPaginacao()}
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default PosicaoCobranca;
